import { useQuery } from "@tanstack/react-query";
import { apiGet } from "./client";
import { useTicketSize } from "../ticket-size";

/**
 * One row of athena_bfsi_rbp.wal_grouping -- same source/shape as
 * risk-pricing-engine's own lib/api/weighted-average-life.ts. `segmentWal` is
 * that row's own wal1 -- the backend already resolved which of wal1/wal2/wal3
 * to read, so nothing here branches on wal2/wal3.
 */
export interface WalGroupingRow {
  industry: string;
  officeNumber: number | null;
  officeName: string | null;
  rating: string;
  segmentWal: number;
  portfolioWal: number;
}

export const ALL_RATINGS_LABEL = "All Ratings";

export function useWeightedAverageLife() {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["pp-wal-grouping", ticket ?? "all"] as const,
    queryFn: () => apiGet<WalGroupingRow[]>("/weighted-average-life", { ticket }),
    staleTime: 5 * 60_000,
  });
}

/**
 * The one row matching a given (industry, office, rating) selection -- same
 * sentinel convention wal_grouping itself uses (NULL office_number,
 * rating = "All Ratings").
 */
export function matchRow(
  rows: WalGroupingRow[],
  industry: string,
  office: number | undefined,
  rating: string | undefined,
): WalGroupingRow | undefined {
  return rows.find(
    (row) =>
      row.industry === industry &&
      (office == null ? row.officeNumber == null : row.officeNumber === office) &&
      (row.rating === (rating ?? ALL_RATINGS_LABEL)),
  );
}

/** Which chart the trend endpoint serves: one point per year, or a rolling 12-month window. */
export type WalTrendView = "annual" | "trailing_12mo";

/**
 * One x-axis date of the trend chart, with both lines -- same shape as risk-pricing-engine's. `xAxis` is the view's
 * own chart date (annual_chart_x_axis / trailing_12mo_chart_x_axis on athena_bfsi_rbp.wal_trend_summary); either WAL is
 * null when that population has no contracts in the selection.
 */
export interface WalTrendPoint {
  xAxis: string;
  realizedWal: number | null;
  activeWal: number | null;
}

/**
 * `rating` here is the REAL grade string ("A"/"B1"/...) or `undefined` for
 * "All Ratings" -- NOT the wal_grouping-specific "All Ratings" sentinel
 * `matchRow` uses above. The trend endpoint filters blended_net_rav's own
 * lo_risk_rating directly, which never holds that sentinel value.
 */
export function useWeightedAverageLifeTrend(
  office: number | undefined,
  industry: string | undefined,
  rating: string | undefined,
  view: WalTrendView,
) {
  const { ticket } = useTicketSize();
  return useQuery({
    queryKey: ["pp-wal-trend", office ?? null, industry ?? null, rating ?? null, view, ticket ?? null] as const,
    queryFn: () =>
      apiGet<WalTrendPoint[]>("/weighted-average-life-trend", {
        office: office != null ? String(office) : undefined,
        industry,
        rating,
        view,
        ticket,
      }),
    staleTime: 60_000,
  });
}
